import React, { useState } from "react";
import {
  Card,
  Title,
  Text,
  Group,
  Badge,
  Stack,
  Button,
  Modal,
  Textarea,
  Table,
  Divider,
  Loader,
  Alert,
} from "@mantine/core";
import { IconEdit, IconCheck, IconX } from "@tabler/icons-react";
import {
  useCEOEndorsement,
  useSendCEOEndorsementForApproval,
  useApproveCEOEndorsement,
  useRejectCEOEndorsement,
} from "../hooks";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../../auth/AuthContext";

export const CEOEndorsementDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const sendForApproval = useSendCEOEndorsementForApproval();
  const approveEndorsement = useApproveCEOEndorsement();
  const rejectEndorsement = useRejectCEOEndorsement();
  const { data: endorsementData, isLoading, error } = useCEOEndorsement(id || "");

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [comments, setComments] = useState("");

  if (isLoading) {
    return (
      <Stack gap="lg" align="center">
        <Loader />
        <Text size="xl">Loading endorsement details...</Text>
      </Stack>
    );
  }

  if (error || !endorsementData) {
    return (
      <Stack gap="lg" align="center">
        <Alert color="red" title="Error">
          Failed to load endorsement details
        </Alert>
      </Stack>
    );
  }

  const endorsement = endorsementData;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "MVR",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleApprove = () => {
    approveEndorsement.mutate(
      { id: endorsement.id, data: { comments: comments.trim() || undefined } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["ceoEndorsement", id] });
          setShowApproveModal(false);
          setComments("");
        },
      }
    );
  };

  const handleReject = () => {
    if (!comments.trim()) {
      return;
    }
    rejectEndorsement.mutate(
      { id: endorsement.id, data: { comments: comments.trim() } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ["ceoEndorsement", id] });
          setShowRejectModal(false);
          setComments("");
        },
      }
    );
  };

  const headApproval = endorsement.approvals?.find((a) => a.stage === "HEAD_OF_PROCUREMENT");
  const ceoApproval = endorsement.approvals?.find((a) => a.stage === "CEO");
  const pendingApproval = endorsement.approvals?.find((a) => a.status === "PENDING");

  const canApprove = pendingApproval && (
    (user?.role === "HEAD_OF_PROCUREMENT" && pendingApproval.stage === "HEAD_OF_PROCUREMENT") ||
    (user?.role === "CEO" && pendingApproval.stage === "CEO") ||
    user?.role === "ADMIN"
  );

  const canReject = pendingApproval && (
    (user?.role === "HEAD_OF_PROCUREMENT" && pendingApproval.stage === "HEAD_OF_PROCUREMENT") ||
    (user?.role === "CEO" && pendingApproval.stage === "CEO") ||
    user?.role === "ADMIN"
  );

  return (
    <Stack gap="lg">
      <Card>
        <Group justify="space-between" mb="md">
          <div>
            <Title order={2}>CEO Endorsement</Title>
            <Text c="dimmed" size="sm">
              Endorsement Details
            </Text>
          </div>
          <Group>
            {endorsement.status === "DRAFT" && (
              <>
                <Button
                  variant="outline"
                  leftSection={<IconEdit size={16} />}
                  onClick={() => navigate(`/bid-committee/endorsements/${endorsement.id}/edit`)}
                >
                  Edit
                </Button>
                <Button
                  onClick={() => sendForApproval.mutate(endorsement.id)}
                  loading={sendForApproval.isPending}
                >
                  Send For Approval
                </Button>
              </>
            )}
            <Badge
              color={
                endorsement.status === "DRAFT"
                  ? "gray"
                  : endorsement.status === "APPROVED"
                  ? "green"
                  : endorsement.status === "REJECTED"
                  ? "red"
                  : "yellow"
              }
              size="lg"
            >
              {endorsement.status}
            </Badge>
          </Group>
        </Group>

        <Table>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td style={{ width: "200px" }}>
                <Text fw={500}>Meeting</Text>
              </Table.Td>
              <Table.Td>
                <Text>
                  {endorsement.meeting?.meetingNumber} - {endorsement.meeting?.title}
                </Text>
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>
                <Text fw={500}>Created</Text>
              </Table.Td>
              <Table.Td>
                <Text>{formatDate(endorsement.createdAt)}</Text>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Card>

      <Card>
        <Title order={3} mb="md">
          Endorsement Items
        </Title>
        <Stack gap="md">
          {endorsement.items.map((item, index) => (
            <Card key={item.id || index} withBorder p="md">
              <Stack gap="sm">
                <Group justify="space-between">
                  <Badge size="lg">
                    {item.agendaItem
                      ? `${item.agendaItem.orderNo}. ${item.agendaItem.subject}`
                      : "Agenda Item"}
                  </Badge>
                </Group>

                <Table>
                  <Table.Tbody>
                    {item.tenderQuotationNumber && (
                      <Table.Tr>
                        <Table.Td style={{ width: "200px" }}>
                          <Text fw={500}>Tender/Quotation Number</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text>{item.tenderQuotationNumber}</Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                    <Table.Tr>
                      <Table.Td>
                        <Text fw={500}>Vendor (Thaana)</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          style={{
                            direction: "rtl",
                            textAlign: "right",
                            fontFamily: "Faruma, Arial, sans-serif",
                          }}
                        >
                          {item.vendor}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td>
                        <Text fw={500}>Project Name (Thaana)</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          style={{
                            direction: "rtl",
                            textAlign: "right",
                            fontFamily: "Faruma, Arial, sans-serif",
                          }}
                        >
                          {item.projectName}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td>
                        <Text fw={500}>Price</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text>{formatCurrency(Number(item.price))}</Text>
                      </Table.Td>
                    </Table.Tr>
                    <Table.Tr>
                      <Table.Td>
                        <Text fw={500}>Decision (Thaana)</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          style={{
                            direction: "rtl",
                            textAlign: "right",
                            fontFamily: "Faruma, Arial, sans-serif",
                          }}
                        >
                          {item.decision}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                    {item.detail && (
                      <Table.Tr>
                        <Table.Td>
                          <Text fw={500}>Detail (Thaana)</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text
                            style={{
                              direction: "rtl",
                              textAlign: "right",
                              fontFamily: "Faruma, Arial, sans-serif",
                            }}
                          >
                            {item.detail}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Stack>
            </Card>
          ))}
        </Stack>
      </Card>

      <Card>
        <Title order={3} mb="md">
          Approval Workflow
        </Title>
        <Stack gap="md">
          {headApproval && (
            <div>
              <Group justify="space-between" mb="xs">
                <Text fw={500}>Head of Procurement</Text>
                <Badge
                  color={
                    headApproval.status === "APPROVED"
                      ? "green"
                      : headApproval.status === "REJECTED"
                      ? "red"
                      : "yellow"
                  }
                >
                  {headApproval.status}
                </Badge>
              </Group>
              {headApproval.approver && (
                <Text size="sm" c="dimmed">
                  {headApproval.approver.fullName}
                </Text>
              )}
              {headApproval.approvedAt && (
                <Text size="sm" c="dimmed">
                  Approved: {formatDate(headApproval.approvedAt)}
                </Text>
              )}
              {headApproval.rejectedAt && (
                <Text size="sm" c="dimmed">
                  Rejected: {formatDate(headApproval.rejectedAt)}
                </Text>
              )}
              {headApproval.comments && (
                <Text size="sm" mt="xs">
                  Comments: {headApproval.comments}
                </Text>
              )}
            </div>
          )}

          {ceoApproval && (
            <>
              <Divider />
              <div>
                <Group justify="space-between" mb="xs">
                  <Text fw={500}>CEO</Text>
                  <Badge
                    color={
                      ceoApproval.status === "APPROVED"
                        ? "green"
                        : ceoApproval.status === "REJECTED"
                        ? "red"
                        : "yellow"
                    }
                  >
                    {ceoApproval.status}
                  </Badge>
                </Group>
                {ceoApproval.approver && (
                  <Text size="sm" c="dimmed">
                    {ceoApproval.approver.fullName}
                  </Text>
                )}
                {ceoApproval.approvedAt && (
                  <Text size="sm" c="dimmed">
                    Approved: {formatDate(ceoApproval.approvedAt)}
                  </Text>
                )}
                {ceoApproval.rejectedAt && (
                  <Text size="sm" c="dimmed">
                    Rejected: {formatDate(ceoApproval.rejectedAt)}
                  </Text>
                )}
                {ceoApproval.comments && (
                  <Text size="sm" mt="xs">
                    Comments: {ceoApproval.comments}
                  </Text>
                )}
              </div>
            </>
          )}

          {canApprove && (
            <Group mt="md">
              <Button
                leftSection={<IconCheck size={16} />}
                color="green"
                onClick={() => setShowApproveModal(true)}
              >
                Approve
              </Button>
            </Group>
          )}

          {canReject && (
            <Group mt="md">
              <Button
                leftSection={<IconX size={16} />}
                color="red"
                variant="outline"
                onClick={() => setShowRejectModal(true)}
              >
                Reject
              </Button>
            </Group>
          )}
        </Stack>
      </Card>

      {/* Approve Modal */}
      <Modal
        opened={showApproveModal}
        onClose={() => {
          setShowApproveModal(false);
          setComments("");
        }}
        title="Approve CEO Endorsement"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to approve this CEO endorsement?
          </Text>
          <Textarea
            label="Comments (Optional)"
            placeholder="Add any comments about your approval..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setShowApproveModal(false);
                setComments("");
              }}
              disabled={approveEndorsement.isPending}
            >
              Cancel
            </Button>
            <Button
              color="green"
              onClick={handleApprove}
              loading={approveEndorsement.isPending}
            >
              Approve
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Reject Modal */}
      <Modal
        opened={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setComments("");
        }}
        title="Reject CEO Endorsement"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Are you sure you want to reject this CEO endorsement? Please provide a reason for rejection.
          </Text>
          <Textarea
            label="Rejection Comments"
            placeholder="Please provide a reason for rejecting this endorsement..."
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            required
            error={!comments.trim() && "Comments are required"}
          />
          <Group justify="flex-end" mt="md">
            <Button
              variant="light"
              onClick={() => {
                setShowRejectModal(false);
                setComments("");
              }}
              disabled={rejectEndorsement.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleReject}
              loading={rejectEndorsement.isPending}
              disabled={!comments.trim()}
            >
              Reject
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};





